import { OrderDiscount } from '@models/order';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { orderActions } from '@store/order/orderSlice';
import { makeItemKey } from '@utils/orderUtils';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';

import { DiscountRowProps } from '../types';

function useItemCoupon({ item, coupon }: DiscountRowProps) {
    const { discounts, totalAmount, orderLines } = useCalculatedRequest();
    const dispatch = useDispatch();

    // 쿠폰 적용 대상 상품+옵션 조합을 서버 전송용 고유 키로 변환
    const itemKey = useMemo(() => makeItemKey(item), [item]);

    const isSelected = useMemo(
        () => !!discounts?.find(
            (it) => it.itemKey === itemKey
                && it.couponId === coupon.id
            && it.idx === item.idx,
        ),
        [discounts, itemKey, coupon, item],
    );

    const getDiscountAmount = () :number => {
        const options = [...item.defaultOptions, ...item.personalOptions]
            .map((option) => option.options)
            .flat();
        const optionPrice = options
            .map((option) => option.quantity * option.optionPrice)
            .reduce((a, b) => a + b, 0);

        // 음수 옵션을 대비하여 해당 가격을 빼는 로직
        const totalPrice = item.itemPrice + Math.min(optionPrice, 0);

        if (coupon.discountMethod === 'RATE') {
            return Math.floor((totalPrice * (coupon.discountValue! / 100)));
        }
        if (coupon.discountMethod === 'BOGO') {
            // 1+1의 경우 옵션의 금액도 포함하여 계산한다
            return Math.max(item.itemPrice + optionPrice, 0);
        }
        // FIXED: 정액 할인이 상품 금액을 초과하지 않도록 cap
        return Math.min(totalPrice, coupon.discountValue!);
    };

    const selectedCoupon = useMemo(
        () => {
            const matchedCoupon = discounts?.find((it) => it.idx === item.idx && it.itemKey === itemKey);
            if (matchedCoupon) return matchedCoupon;

            // 직접 매칭이 없을 때 BOGO 처리:
            // 앞방향 우선으로 빈 슬롯을 탐색하고, 앞에 빈 슬롯이 없을 때만 뒷방향으로 fallback
            // → 한 BOGO 엔트리가 양쪽 모두에 표시되는 문제 방지
            const maxIdx = (orderLines.find((it) => makeItemKey(it) === itemKey)?.quantity ?? 1) - 1;
            const coveredIdxSet = new Set(
                (discounts || [])
                    .filter((it) => it.itemKey === itemKey && it.idx != null)
                    .map((it) => it.idx as number),
            );
            const pairedBogo = (discounts || []).find((bogoEntry) => {
                if (bogoEntry.itemKey !== itemKey || bogoEntry.discountMethod !== 'BOGO' || bogoEntry.idx == null) return false;
                let nextIdx = bogoEntry.idx + 1;
                while (nextIdx <= maxIdx && coveredIdxSet.has(nextIdx)) nextIdx += 1;
                if (nextIdx <= maxIdx) return nextIdx === item.idx;
                let prevIdx = bogoEntry.idx - 1;
                while (prevIdx >= 0 && coveredIdxSet.has(prevIdx)) prevIdx -= 1;
                return prevIdx >= 0 && prevIdx === item.idx;
            });
            if (pairedBogo) return pairedBogo;
            return undefined;
        },
        [discounts, itemKey, item],
    );
    const disabled = selectedCoupon && (!isSelected);

    const getBogoEnabled = () => {
        /*
        const appliedCount = discounts?.filter((it) => it.itemKey === itemKey)
            .length || 0;
         */
        const bogoCount = discounts?.filter((it) => it.itemKey === itemKey && it.discountMethod === 'BOGO')
            .length || 0;
        const normalCount = discounts?.filter((it) => it.itemKey === itemKey && it.discountMethod !== 'BOGO')
            .length || 0;
        const enabledCount = orderLines.find((it) => {
            const enabledItemKey = makeItemKey(it);
            return itemKey === enabledItemKey;
        })?.quantity || 0;
        return enabledCount - bogoCount * 2 - normalCount >= 2;
    };

    const selectCoupon = () => {
        if (disabled) {
            return;
        }
        if (isSelected) {
            dispatch(orderActions.removeCoupon(coupon.id));
            return;
        }
        // 같은 상품에 2개이상 쿠폰이 적용 가능할 때 1+1 쿠폰 적용 가능
        if (!getBogoEnabled() && coupon.discountMethod === 'BOGO') {
            toast.info('쿠폰 사용 개수를 초과했습니다.');
            return;
        }
        // 선택되지 않은 경우 추가
        const newDiscount: OrderDiscount[] = [...(discounts || []).filter((it) => it.couponId !== coupon.id && it.discountScope === 'ITEM')];
        const discountAmount = getDiscountAmount();

        if (coupon.minOrderAmount && coupon.minOrderAmount > totalAmount) {
            toast.error(`최소 주문 금액 ${coupon.minOrderAmount}원 이상일 때 사용 가능합니다.`);
            return;
        }

        newDiscount.push({
            type: 'COUPON',
            couponId: coupon.id,
            couponName: coupon.title,
            discountScope: 'ITEM',
            discountAmount,
            itemId: item.shopItemId,
            itemName: item.itemName,
            itemKey,
            idx: item.idx,
            discountMethod: coupon.discountMethod,
        });

        dispatch(orderActions.setDiscounts(newDiscount));
    };

    return {
        isSelected,
        selectCoupon,
        selectedCoupon,
        disabled,
    };
}

export default useItemCoupon;
